import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Col, Divider, Row, Typography } from 'antd'
import dayjs from "dayjs";

import { API_DYDIFF } from '../api/api'
import { axiosPost } from '../services/http.services'
import { ServerKeys } from '../utils/serverKeys'
import CustomSelect from '../customs/CustomSelect'
import { labels } from '../resources'
import CustomButton from '../customs/CustomButton'
import CustomTable from '../customs/CustomTable'
import { formatIndianStyle, getTimestamp } from '../helper/arrayFormatter'
import CustomFilter from '../common/CustomFilter'

const html2pdf = require('html2pdf.js');
const XLSX = require('xlsx');
const { saveAs } = require('file-saver');


const { Title } = Typography;


const Dydiff = forwardRef(({ airportOptions, airlineOptions }, ref) => {
  const [selectedAirport, setSelectedAirport] = useState(undefined);
  const [activeFilters, setActiveFilters] = useState({
    airports: [],
    statuses: [],
    years: [],
    months: [],
    start_date: '',
    end_date: '',
  });
  const [tableData, setTableData] = useState([])
  const [tableLoader, setTableLoader] = useState(false)
  const [isDownloadPdf, setIsDownloadPdf] = useState(false)

  const airlineNames = useMemo(
    () => (airlineOptions || []).map((item) => item?.value).filter(Boolean),
    [airlineOptions]
  );

  const preferredOrder = useMemo(
    () => ([
      "Airport",
      "Date",
      "TOTAL DEP PAX",
      "DY DIFF",
      "AL SUM",
      "DYPAX",
      "ENTRY APP",
      "KIOSKS",
      "PESC",
      "BOARDING",
      ...airlineNames,
    ]),
    [airlineNames]
  );

  const buildPayload = useCallback((filters = activeFilters) => {
    const payload = {};

    if (filters.airports?.length) {
      payload.Airport = filters.airports;
    }
    if (filters.statuses?.length) {
      payload.Status = filters.statuses;
    }
    if (filters.years?.length) {
      payload.Year = filters.years;
    }
    if (filters.months?.length) {
      payload.MONTH_YEAR = filters.months;
    }
    if (filters.start_date || filters.end_date) {
      payload.Date = {};
      if (filters.start_date) {
        payload.Date.start = filters.start_date;
      }
      if (filters.end_date) {
        payload.Date.end = filters.end_date;
      }
    }

    return payload;
  }, [activeFilters]);

  const loadRows = useCallback(async (filters = activeFilters) => {
    if (!filters.airports?.length) {
      setTableData([]);
      setTableLoader(false);
      return;
    }

    setTableLoader(true);
    try {
      const response = await axiosPost(API_DYDIFF, buildPayload(filters));
      const rows = (response?.data || []).map((item) => {
        const nextItem = [...item];
        nextItem[1] = nextItem[1] ? dayjs(nextItem[1]).format('DD/MM/YYYY') : "";
        return nextItem;
      });
      setTableData(rows);
    } catch (error) {
      console.log(error || "Something went wrong");
      setTableData([]);
    }
    setTableLoader(false);
  }, [activeFilters, buildPayload]);

  useEffect(() => {
    loadRows(activeFilters);
  }, [activeFilters, loadRows]);

  useImperativeHandle(ref, () => ({
    setAirport: (option) => {
      const airports = Array.isArray(option) ? option : option ? [option] : [];
      setSelectedAirport(airports[0]);
      setActiveFilters((current) => ({ ...current, airports }));
    },
    setContext: (context = {}) => {
      const nextAirports = context.airports || [];
      setSelectedAirport(nextAirports[0]);
      setActiveFilters({
        airports: nextAirports,
        statuses: context.statuses || [],
        years: context.years || [],
        months: context.months || [],
        start_date: context.start_date || '',
        end_date: context.end_date || '',
      });
    }
  }));

  const downloadPageAsPdf = (idName, exportName) => {
    setIsDownloadPdf(true)

    const element = document.getElementById(idName);
    if (!element) {
      setIsDownloadPdf(false);
      return;
    }

    const contentWidth = element.offsetWidth;
    const contentHeight = element.offsetHeight;

    const scaleFactor = 0.4;
    const pdfWidth = contentWidth * scaleFactor;
    const pdfHeight = contentHeight * scaleFactor;

    const options = {
      image: { type: 'jpeg', quality: 1 },
      html2canvas: {
        scale: 3,
        letterRendering: true,
        logging: false
      },
      jsPDF: {
        unit: 'mm',
        format: [pdfWidth, pdfHeight],
        orientation: 'landscape'
      }
    };

    const timestamp = getTimestamp();
    const filename = `${exportName}_${timestamp}.pdf`;

    html2pdf()
      .from(element)
      .set(options)
      .save(filename);

    setTimeout(() => {
      setIsDownloadPdf(false)
    }, 500);
  };

  const exportToExcel = (name, currentTableData) => {
    const workbook = XLSX.utils.book_new();
    const rows = currentTableData.map((row) => [...row]);
    rows.unshift(preferredOrder);
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'DYDIFF');

    const excelFile = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const timestamp = getTimestamp();
    const filename = `${name}_${timestamp}.xlsx`;
    saveAs(new Blob([excelFile]), filename);
  };

  const RenderTitle = ({ title, idName, exportName, exportData, style = {} }) => {
    return (
      <>
        <div style={{ position: "relative", ...style }}>
          <Title level={4} style={{ borderRadius: "5px 25px", padding: "5px 25px", marginTop: 20, textAlign: "center" }}>
            {title}
          </Title>

          {!isDownloadPdf && <div style={{ position: "absolute", top: 7, right: 30 }}>
            <CustomButton className="ml-2" icon={<img src={require("../assests/download.png")} alt="download" loading="lazy"
              style={{ width: 15, height: 15 }} />}
              label={"PDF"}
              onClick={() => downloadPageAsPdf(idName, exportName)}
            />

            <CustomButton className="ml-2" icon={<img src={require("../assests/download.png")} alt="download" loading="lazy"
              style={{ width: 15, height: 15 }} />}
              label={"XLSX"}
              onClick={() => exportToExcel(exportName, exportData)}
            />
          </div>}
        </div>

        <Divider style={{ margin: 20 }} />
      </>
    )
  }

  const dynamicColumn = useCallback(() => {
    return preferredOrder.map((key, index) => {
      const sorterFunction = (aValue, bValue) => {
        if ([aValue[0], bValue[0]].includes("TOTAL")) {
          return 0
        }
        if (typeof aValue[index] === "string" && typeof bValue[index] === "string") {
          return aValue[index].localeCompare(bValue[index]);
        }

        if (!isNaN(aValue[index]) && !isNaN(bValue[index])) {
          return aValue[index] - bValue[index];
        }
        return 0;
      }

      return {
        title: <CustomFilter
          key={index}
          title={key?.replace(/_/g, ' ')?.toUpperCase()}
          titleClassName={airlineNames.includes(key) ? "text-green" : "text-[#3B82F6]"}
        />,
        align: "center",
        dataIndex: index,
        key: index,
        render: (text, record) => {
          let highlight = false;

          if (index === 5 || index === 6 || index === 7) {
            highlight = true;
          }

          if (index === 4 && record[4] !== record[5]) {
            highlight = true;
          }

          return (
            <div style={{ position: 'relative' }}>
              <p
                style={{
                  position: 'relative',
                  backgroundColor: highlight ? "#D70040" : "",
                  paddingBottom: isDownloadPdf && (highlight || index === 0) ? '8px' : '0px'
                }}
                className={`text-right ${highlight ? "text-white font-bold rounded-sm px-1" : index === 0 ? "font-semibold text-white bg-[#3B82F6] px-2 rounded-sm" : "font-normal"}`}
              >
                {formatIndianStyle(text)}
              </p>
            </div>
          )
        },
        sorter: sorterFunction
      }
    })
  }, [airlineNames, isDownloadPdf, preferredOrder]);

  return (
    <div id='DydiffComp' style={{ marginTop: 50 }}>
      <RenderTitle title="DY DIFF"
        idName="dydiff"
        exportName={"DY DIFF"}
        exportData={tableData}
      />

      <div style={{ flex: 1 }} id="dydiff">
        <Row gutter={[3, 20]} className={"mb-4"} style={{ alignItems: "flex-end" }}>
          <Col className="mr-3">
            <CustomSelect
              placeholder='Select Airport'
              name={ServerKeys.AIRPORT}
              label={labels.Airport}
              options={airportOptions}
              value={selectedAirport}
              onChange={(option) => {
                const airports = option ? [option] : [];
                setSelectedAirport(option);
                setActiveFilters((current) => ({ ...current, airports }));
              }}
            />
          </Col>
        </Row>

        <CustomTable
          rowKey={(record, idx) => `${record?.[0] || 'row'}-${record?.[1] || idx}-${idx}`}
          data={tableData}
          columns={dynamicColumn()}
          bordered={false}
          loading={tableLoader ? !tableData?.length : false}
          scroll={{ x: true }}
          pagination={false}
        />
      </div>
    </div>
  )
});


export default Dydiff;
